import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useTelephonyClient } from '../hooks/useTelephonyClient';
import { installKeyboardShortcuts } from '../lib/telephony/keyboard';
import { usePhoneStore, setLastBrand, getLastBrand } from '../stores/phone-store';
import { DialBlockedError } from '../lib/telephony';
import type { Brand } from '../lib/telephony';

export interface DialOptions {
  brand?: Brand;
  customerId?: string;
  customerName?: string;
  sourceContext?: string;
  /** Belcontext (terugbel-lus, 22-10): de taak of order waarover gebeld wordt; landt op calls.context_*. */
  contextTaskId?: string;
  contextRef?: string;
  contextReden?: string;
}

export interface OpenDialPadOptions {
  number?: string;
  brand?: Brand;
  customerId?: string;
  customerName?: string;
  sourceContext?: string;
  contextTaskId?: string;
  contextRef?: string;
  contextReden?: string;
}

interface PhoneContextShape {
  dial: (number: string, opts?: DialOptions) => Promise<void>;
  openDialPad: (prefill?: OpenDialPadOptions) => void;
  closeDialPad: () => void;
  dialPadOpen: boolean;
}

const PhoneContext = createContext<PhoneContextShape | null>(null);

export function PhoneProvider({ children }: { children: ReactNode }) {
  const { dial: rawDial, answer, reject, hangup, toggleMute, activeCall } = useTelephonyClient();
  const dialPadOpen = usePhoneStore((s) => s.dialPadOpen);
  const setDialPadOpen = usePhoneStore((s) => s.setDialPadOpen);

  useEffect(() => {
    const off = installKeyboardShortcuts({
      onAnswer: () => answer(),
      onReject: () => reject(),
      onHangup: () => hangup(),
      onToggleMute: () => toggleMute(),
      onOpenDialPad: () => setDialPadOpen(true),
      getActiveCallState: () => {
        if (!activeCall) return 'idle';
        if (activeCall.state === 'ringing') return 'ringing';
        if (activeCall.state === 'active' || activeCall.state === 'dialing') return 'active';
        return 'other';
      },
    });
    return off;
  }, [answer, reject, hangup, toggleMute, activeCall, setDialPadOpen]);

  const value = useMemo<PhoneContextShape>(() => ({
    dial: async (number, opts) => {
      const brand = (opts?.brand ?? (getLastBrand() as Brand | null) ?? 'CINCO') as Brand;
      setLastBrand(brand);
      const customer = opts?.customerId && opts?.customerName
        ? { id: opts.customerId, name: opts.customerName, type: 'account' as const }
        : undefined;
      try {
        await rawDial(number, brand, customer, {
          sourceContext: opts?.sourceContext ?? null,
          taskId: opts?.contextTaskId ?? null,
          ref: opts?.contextRef ?? null,
          reden: opts?.contextReden ?? null,
        });
      } catch (err) {
        // Geweigerd omdat er al een gesprek loopt: de dock toont de uitleg al
        // (setCallError → snapshot-banner); niet nogmaals laten knallen bij
        // fire-and-forget callers (CallButton doet `void dial(...)`).
        if (err instanceof DialBlockedError) return;
        throw err;
      }
    },
    openDialPad: (prefill) => setDialPadOpen(true, prefill),
    closeDialPad: () => setDialPadOpen(false),
    dialPadOpen,
  }), [rawDial, dialPadOpen, setDialPadOpen]);

  return <PhoneContext.Provider value={value}>{children}</PhoneContext.Provider>;
}

export function usePhone(): PhoneContextShape {
  const ctx = useContext(PhoneContext);
  if (!ctx) throw new Error('usePhone must be used within PhoneProvider');
  return ctx;
}
